import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Copy } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/brand";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const ROLES = [["admin", "Company Administrator"], ["hse_manager", "HSE Manager"], ["supervisor", "Supervisor"], ["project_manager", "Project Manager"], ["author", "Document Author"], ["reviewer", "Document Reviewer"], ["approver", "Document Approver"], ["trainer", "Trainer"], ["employee", "Employee"], ["contractor", "Contractor"], ["auditor", "Read-only Auditor"]];
const label = (r) => (r === "owner" ? "Organisation Owner" : ROLES.find((x) => x[0] === r)?.[1] || r);

export default function Team() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", role: "employee", job_title: "", department: "" });
  const [link, setLink] = useState(null);
  const load = () => api.get("/org/members").then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, []);
  const invite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/org/members", f);
      setLink(data.invite_link);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const role = async (id, r) => {
    try {
      await api.put(`/org/members/${id}`, { role: r });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  if (!list) return <Loader />;
  const manager = ["owner", "admin", "hse_manager"].includes(user.role);
  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="team-page">
      <div className="flex items-end justify-between"><div><p className="eyebrow">Workforce</p><h1 className="mt-2 text-3xl font-bold">Team & roles</h1><p className="text-sm text-muted-foreground">Permissions are enforced on the server for every action.</p></div>
        {manager && <Button onClick={() => { setOpen(true); setLink(null); }} data-testid="invite-open"><UserPlus className="mr-1.5 h-4 w-4" />Invite member</Button>}</div>
      <div className="overflow-x-auto rounded-2xl border bg-card">
        <table className="w-full text-sm"><thead className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Job title</th><th className="p-4">Role</th><th className="p-4">Status</th><th className="p-4" /></tr></thead>
          <tbody>{list.map((m) => (
            <tr key={m.id} className="border-b last:border-0" data-testid={`member-row-${m.id}`}>
              <td className="p-4 font-medium">{m.name}</td><td className="p-4 text-muted-foreground">{m.email}</td><td className="p-4">{m.job_title || "—"}</td>
              <td className="p-4">{manager && m.role !== "owner" && m.id !== user.id ? (
                <Select value={m.role} onValueChange={(v) => role(m.id, v)}><SelectTrigger className="h-8 w-52" data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger><SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
              ) : label(m.role)}</td>
              <td className="p-4 text-xs">{m.invite_pending ? <span className="text-[#B87300]">Invite pending</span> : <span className="text-health">Active</span>}</td>
              <td className="p-4 text-right">{manager && m.role !== "owner" && m.id !== user.id && <Button variant="ghost" size="sm" className="text-alert" onClick={() => window.confirm(`Remove ${m.name}?`) && api.delete(`/org/members/${m.id}`).then(load)} data-testid={`remove-member-${m.id}`}>Remove</Button>}</td>
            </tr>
          ))}</tbody></table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invite a team member</DialogTitle></DialogHeader>
          {link ? (
            <div className="space-y-3" data-testid="invite-link-box"><p className="text-sm">Invitation created and emailed to {f.email}. You can also share this secure link directly (valid 72 hours):</p>
              <div className="flex gap-2"><Input readOnly value={link} /><Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button></div></div>
          ) : (
            <form onSubmit={invite} className="space-y-3">
              <Input required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name" />
              <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email" />
              <Input placeholder="Job title" value={f.job_title} onChange={(e) => setF({ ...f, job_title: e.target.value })} />
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
              <Button type="submit" className="w-full" data-testid="invite-submit">Create invitation</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
